import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../../components/ui/dropdown-menu';
import { useNotifications } from '../../context/NotificationsContext';
import { Notification, GoodThingNotificationData } from '../../types/notifications';

interface NotificationMenuProps {
  children?: React.ReactNode;
}

/**
 * A dropdown menu for displaying notifications
 */
export const NotificationMenu: React.FC<NotificationMenuProps> = ({ children }) => {
  const { notifications, removeNotification } = useNotifications();
  const navigate = useNavigate();

  const handleNotificationClick = (notification: Notification) => {
    // Handle different notification types
    if (notification.type === 'good-thing') {
      navigate('/good-things/dashboard');
    }
    
    // Remove the notification after clicking
    removeNotification(notification.id);
  };

  // Render notification content based on type
  const renderNotificationContent = (notification: Notification) => {
    switch (notification.type) {
      case 'good-thing':
        const data = notification.data as GoodThingNotificationData;
        return (
          <div className="flex flex-col">
            <span className="font-medium">New Good Thing</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              You've received a new Good Thing from {data.submitterName}
            </span>
          </div>
        );
      default:
        return (
          <div className="flex flex-col">
            <span className="font-medium">New Notification</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              You have a new notification
            </span>
          </div>
        );
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="relative">
          {children || (
            <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
              <Bell className="h-4 w-4" />
            </button>
          )}
          
          {notifications.length > 0 && (
            <div className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full">
              {notifications.length}
            </div>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {notifications.length === 0 ? (
          <DropdownMenuItem disabled>
            <span className="text-gray-500 dark:text-gray-400">No new notifications</span>
          </DropdownMenuItem>
        ) : (
          <>
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className="cursor-pointer py-2"
              >
                {renderNotificationContent(notification)}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationMenu;
